<template>
    <div>
        <div class="row">
            <!-- Nível de Acesso -->
            <div
                class="col-md-4"
            >
                <div class="form-group">
                    <label for="userAccessLevel">
                        <span class="required">*</span>
                        Nível de Acesso
                    </label>
                    <select
                        class="form-control"
                        id="userAccessLevel"
                        :value="value.access_nivel"
                        @change="updateField('access_nivel', $event.target.value)"
                        :disabled="currentUserLevel == 4"
                    >
                        <option value="1">Usuário Padrão</option>
                        <option
                            value="3"
                            v-if="currentUserLevel == 3"
                        >Administrador Master
                        </option>
                    </select>
                </div>
            </div>

            <!-- Centro de Custo -->
            <div class="col-md-4">
                <div class="form-group">
                    <label for="userCostCenter">
                        <span class="required">*</span>
                        Centro de Custo
                    </label>
                    <v-select
                        :options="costCenters"
                        label="description"
                        :reduce="(cost_center) => cost_center.id"
                        class="style-chooser"
                        :value="value.cost_center_id"
                        @input="updateField('cost_center_id', $event)"
                    >
                        <div slot="no-options">Nenhum centro de custo encontrado</div>
                    </v-select>
                </div>
            </div>

            <!-- Código ERP -->
            <div class="col-md-4">
                <div class="form-group">
                    <label for="userErpCode">
                        <span class="required">*</span>
                        Código ERP
                    </label>
                    <input
                        type="text"
                        class="form-control"
                        id="userErpCode"
                        :value="value.erp_code"
                        @input="updateField('erp_code', $event.target.value.toUpperCase())"
                        style="text-transform: uppercase"
                    />
                </div>
            </div>
        </div>

        <!-- Configurações de Funcionalidades -->
        <div class="functionality-settings">
            <div class="settings-header">
                <h6 class="settings-title">
                    <i class="fas fa-toggle-on mr-2"></i>
                    Configurações de Funcionalidades
                </h6>
            </div>

            <div class="row">
                <!-- Checklist Simplificado -->
                <div class="col-md-6 mb-3">
                    <div class="custom-control custom-switch">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            id="switch-simplified-checklist"
                            :checked="value.simplified_checklist"
                            @change="updateField('simplified_checklist', $event.target.checked)"
                        />
                        <label
                            class="custom-control-label"
                            for="switch-simplified-checklist"
                        >
                            <span class="switch-label">
                                <i class="fas fa-clipboard-list mr-2"></i>
                                Checklist Simplificado
                            </span>
                            <small class="switch-description">
                                Utiliza versão reduzida do checklist de veículos
                            </small>
                        </label>
                    </div>
                </div>

                <!-- Aprovador de Reservas -->
                <div class="col-md-6 mb-3">
                    <div class="custom-control custom-switch">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            id="switch-is-approver"
                            :checked="value.is_approver"
                            @change="updateField('is_approver', $event.target.checked)"
                        />
                        <label
                            class="custom-control-label"
                            for="switch-is-approver"
                        >
                            <span class="switch-label">
                                <i class="fas fa-user-check mr-2"></i>
                                Aprovador de Reservas
                            </span>
                            <small class="switch-description">
                                Pode aprovar reservas de veículos compartilhados
                            </small>
                        </label>
                    </div>
                </div>

                <!-- Telemetria -->
                <div class="col-md-6 mb-3">
                    <div class="custom-control custom-switch">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            id="switch-telemetry"
                            :checked="value.telemetry"
                            @change="updateField('telemetry', $event.target.checked)"
                        />
                        <label
                            class="custom-control-label"
                            for="switch-telemetry"
                        >
                            <span class="switch-label">
                                <i class="fas fa-satellite mr-2"></i>
                                Telemetria
                            </span>
                            <small class="switch-description">
                                Possui cursos que permitem ao usuário ser motorista
                            </small>
                        </label>
                    </div>
                </div>

                <!-- Usuário Executivo -->
                <div class="col-md-6 mb-3">
                    <div class="custom-control custom-switch">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            id="switch-is-executive"
                            :checked="value.is_executive"
                            @change="updateField('is_executive', $event.target.checked)"
                        />
                        <label
                            class="custom-control-label"
                            for="switch-is-executive"
                        >
                            <span class="switch-label">
                                <i class="fas fa-crown mr-2"></i>
                                Usuário Executivo
                            </span>
                            <small class="switch-description">
                                Gerente ou superior com direito a dirigir veículos executivos
                            </small>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <!-- Resumo das Configurações -->
        <div
            class="settings-summary"
            v-if="hasActiveSettings"
        >
            <div class="summary-header">
                <i class="fas fa-clipboard-check mr-2"></i>
                Resumo das Configurações Ativas
            </div>
            <div class="summary-content">
                <div
                    class="summary-item"
                    v-if="value.simplified_checklist"
                >
                    <span class="badge badge-info">
                        <i class="fas fa-clipboard-list mr-1"></i>
                        Checklist Simplificado
                    </span>
                </div>
                <div
                    class="summary-item"
                    v-if="value.is_approver"
                >
                    <span class="badge badge-success">
                        <i class="fas fa-user-check mr-1"></i>
                        Aprovador
                    </span>
                </div>
                <div
                    class="summary-item"
                    v-if="value.telemetry"
                >
                    <span class="badge badge-warning">
                        <i class="fas fa-satellite mr-1"></i>
                        Telemetria
                    </span>
                </div>
                <div
                    class="summary-item"
                    v-if="value.is_executive"
                >
                    <span class="badge badge-primary">
                        <i class="fas fa-crown mr-1"></i>
                        Executivo
                    </span>
                </div>
                <div
                    class="summary-item"
                    v-if="value.access_nivel == 3"
                >
                    <span class="badge badge-danger">
                        <i class="fas fa-user-shield mr-1"></i>
                        Admin Master
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from "vue-select";

export default {
    name: 'UserSystemInfo',
    components: {
        vSelect,
    },
    props: {
        value: {
            type: Object,
            required: true
        },
        costCenters: {
            type: Array,
            default: () => []
        },
        isEditing: {
            type: Boolean,
            default: false
        },
        currentUserLevel: {
            type: [String, Number],
            default: 1
        },
    },
    computed: {
        hasActiveSettings() {
            return this.value.simplified_checklist ||
                this.value.is_approver ||
                this.value.telemetry ||
                this.value.is_executive ||
                this.value.access_nivel == 3;
        }
    },
    methods: {
        updateField(field, value) {
            this.$emit('update', field, value);
        },
    }
};
</script>

<style scoped>

.functionality-settings {
    background: #FFF;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
    border: 1px solid #dee2e6;
}

.settings-header {
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #dee2e6;
}

.settings-title {
    color: #495057;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    font-size: 1rem;
}

.custom-control-label {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: 1.5rem;
}

.switch-label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 2px;
}

.switch-description {
    color: #6c757d;
    font-size: 0.875rem;
    font-style: italic;
}

.settings-summary {
    background: #FFF;
    border-radius: 8px;
    padding: 15px;
    border: 1px solid #dee2e6;
    margin-top: 20px;
}

.summary-header {
    font-weight: 600;
    color: #495057;
    margin-bottom: 10px;
    font-size: 14px;
}

.summary-content {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.summary-item {
    display: inline-block;
}

.badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
}

.badge-info {
    background-color: #17a2b8;
    color: white;
}

.badge-success {
    background-color: #28a745;
    color: white;
}

.badge-warning {
    background-color: #ffc107;
    color: #212529;
}

.badge-primary {
    background-color: #FE8D18;
    color: white;
}

.badge-danger {
    background-color: #dc3545;
    color: white;
}

/* Customização do v-select */
.style-chooser >>> .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 2px 6px;
}

.style-chooser >>> .vs__dropdown-toggle:hover {
    border-color: #80bdff;
}

.style-chooser >>> .vs__selected {
    color: #495057;
}

.style-chooser >>> .vs__search::placeholder {
    color: #6c757d;
}

/* Responsividade */
@media (max-width: 768px) {
    .user-system-info {
        padding: 15px;
        margin-bottom: 15px;
    }

    .functionality-settings {
        padding: 15px;
    }

    .section-title,
    .settings-title {
        font-size: 1rem;
    }

    .form-control {
        font-size: 16px; /* Evita zoom no iOS */
    }

    .summary-content {
        flex-direction: column;
        align-items: flex-start;
    }

    .badge {
        margin-bottom: 5px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/User/form-components/UserSystemInfo.vue